// Interaction primitives adapted from Interior (MIT): https://www.interior.dev
// Styling intentionally uses this app's design tokens rather than Interior's defaults.
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { HTMLAttributes, HTMLInputAutoCompleteAttribute, HTMLInputTypeAttribute, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode, RefObject } from 'react'
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion, useTransform } from 'motion/react'
import type { MotionValue, Transition } from 'motion/react'

const cn = (...classes: (string | number | false | null | undefined)[]) => classes.filter(Boolean).join(' ')

const CROSSFADE: Transition = { type: 'spring', stiffness: 260, damping: 34, mass: 0.8 }
const INSTANT: Transition = { duration: 0 }

export type PressDepth = {
  pressed: boolean
  ref: (next: Element | null) => void
  bind: {
    onPointerDown: (event: ReactPointerEvent) => void
    onKeyDown: (event: ReactKeyboardEvent) => void
    onKeyUp: (event: ReactKeyboardEvent) => void
    onBlur: () => void
  }
}

export function usePressDepth({ disabled = false }: { disabled?: boolean } = {}): PressDepth {
  const [pressed, setPressed] = useState(false)
  const [tracking, setTracking] = useState(false)
  const node = useRef<Element | null>(null)
  const pointer = useRef<number | null>(null)

  const stop = useCallback(() => {
    pointer.current = null
    setTracking(false)
    setPressed(false)
  }, [])

  useEffect(() => {
    if (!tracking) return undefined
    const contains = (event: PointerEvent) => {
      const rect = node.current?.getBoundingClientRect()
      return Boolean(rect && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom)
    }
    const move = (event: PointerEvent) => { if (event.pointerId === pointer.current) setPressed(contains(event)) }
    const lift = (event: PointerEvent) => { if (event.pointerId === pointer.current) stop() }
    const hidden = () => { if (document.hidden) stop() }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', lift)
    window.addEventListener('pointercancel', lift)
    window.addEventListener('blur', stop)
    document.addEventListener('visibilitychange', hidden)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', lift)
      window.removeEventListener('pointercancel', lift)
      window.removeEventListener('blur', stop)
      document.removeEventListener('visibilitychange', hidden)
    }
  }, [tracking, stop])

  useEffect(() => { if (disabled) stop() }, [disabled, stop])

  return {
    pressed,
    ref: useCallback((next: Element | null) => { node.current = next }, []),
    bind: {
      onPointerDown: (event) => {
        if (disabled || (event.pointerType === 'mouse' && event.button !== 0)) return
        pointer.current = event.pointerId
        setTracking(true)
        setPressed(true)
      },
      onKeyDown: (event) => { if (!disabled && !event.repeat && [' ', 'Enter'].includes(event.key)) setPressed(true) },
      onKeyUp: (event) => { if ([' ', 'Enter', 'Escape'].includes(event.key)) setPressed(false) },
      onBlur: stop,
    },
  }
}

function Spinner({ still }: { still: boolean }) {
  return (
    <motion.svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0" animate={still ? undefined : { rotate: 360 }} transition={still ? undefined : { duration: 0.85, repeat: Infinity, ease: 'linear' }}>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" opacity="0.22" />
      <path d="M10.5 6A4.5 4.5 0 0 0 6 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </motion.svg>
  )
}

export type LoadingContentProps = {
  loading?: boolean
  label?: ReactNode
  children?: ReactNode
}

export function LoadingContent({ loading, children, label = 'Working…' }: LoadingContentProps) {
  const reduced = useReducedMotion()
  const transition = reduced ? INSTANT : CROSSFADE
  return (
    <span aria-hidden="true" className="grid place-items-center">
      <motion.span className="col-start-1 row-start-1 flex items-center gap-1.5" initial={false} animate={{ opacity: loading ? 0 : 1, y: loading ? -2 : 0 }} transition={transition}>{children}</motion.span>
      <motion.span className="col-start-1 row-start-1 flex items-center gap-1.5" initial={false} animate={{ opacity: loading ? 1 : 0, y: loading ? 0 : 2 }} transition={transition}><Spinner still={reduced || !loading} />{label}</motion.span>
    </span>
  )
}

function ThinkingDot({ index, wave }: { index: number; wave: MotionValue<number> }) {
  const lift = useTransform(wave, (value) => {
    let distance = (value - index) % 3
    if (distance < 0) distance += 3
    if (distance > 1.5) distance -= 3
    return Math.max(0, 1 - Math.abs(distance))
  })
  const y = useTransform(lift, [0, 1], [0, -2])
  const opacity = useTransform(lift, [0, 1], [0.35, 1])
  return <motion.span className="block size-1 rounded-full bg-muted-500" style={{ y, opacity }} />
}

export type ThinkingIndicatorProps = {
  name?: string | null
  label?: string
  className?: string
}

export function ThinkingIndicator({ name = 'Assistant', label = 'Thinking', className = '' }: ThinkingIndicatorProps) {
  const reduced = useReducedMotion()
  const wave = useMotionValue(0)
  useEffect(() => {
    if (reduced) {
      wave.jump(0)
      return undefined
    }
    const controls = animate(wave, 3, { duration: 1.2, ease: 'linear', repeat: Infinity })
    return () => controls.stop()
  }, [reduced, wave])

  return (
    <motion.div className={cn('flex h-6 items-center gap-2 text-xs/4 text-muted-600', className)} initial={reduced ? { opacity: 0 } : { opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={reduced ? INSTANT : CROSSFADE} role="status" aria-live="polite" aria-label={`${name}: ${label}`}>
      <span aria-hidden="true">{label}</span>
      <span className="flex items-center gap-1" aria-hidden="true">
        {[0, 1, 2].map((index) => reduced ? <span key={index} className="size-1 rounded-full bg-muted-500" /> : <ThinkingDot key={index} index={index} wave={wave} />)}
      </span>
    </motion.div>
  )
}

const digitsOnly = (text: string) => text.replace(/\D/g, '')

export type OtpInputProps = {
  length?: number
  value: string
  onChange: (code: string) => void
  error?: string | string[] | null
  disabled?: boolean
  autoFocus?: boolean
  label?: string
}

export function OtpInput({ length = 6, value, onChange, error, disabled = false, autoFocus = false, label = 'Verification code' }: OtpInputProps) {
  const reduced = useReducedMotion()
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const chars = Array.from({ length }, (_, index) => value[index] || '')
  const [focused, setFocused] = useState(-1)
  const statusId = useId()

  const focusAt = useCallback((index: number) => {
    const next = refs.current[Math.max(0, Math.min(length - 1, index))]
    next?.focus()
    next?.select()
  }, [length])

  useEffect(() => { if (autoFocus && !disabled) focusAt(0) }, [autoFocus, disabled, focusAt])
  useEffect(() => { if (error && !disabled) focusAt(0) }, [error, disabled, focusAt])

  const commit = (next: string[]) => onChange(next.join('').slice(0, length))
  const fillFrom = (index: number, text: string) => {
    const incoming = digitsOnly(text)
    if (!incoming) return
    const next = [...chars]
    let cursor = index
    for (const character of incoming) {
      if (cursor >= length) break
      next[cursor] = character
      cursor += 1
    }
    commit(next)
    focusAt(cursor)
  }

  return (
    <div className="flex flex-col">
      <motion.div role="group" aria-label={label} className="flex gap-2" initial={false} animate={error && !reduced ? { x: [0, -5, 4, -3, 0] } : { x: 0 }} transition={{ duration: 0.32 }}>
        {chars.map((char, index) => (
          <div key={index} className={cn('relative h-12 w-10', index === 3 && 'ml-2')}>
            <input
              ref={(node) => { refs.current[index] = node }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              aria-label={`${label}, digit ${index + 1} of ${length}`}
              aria-invalid={Boolean(error) || undefined}
              aria-describedby={error ? statusId : undefined}
              disabled={disabled}
              value={char}
              onFocus={(event) => { setFocused(index); event.currentTarget.select() }}
              onBlur={(event) => { if (!refs.current.some((node) => node === event.relatedTarget)) setFocused(-1) }}
              onPaste={(event) => { event.preventDefault(); fillFrom(digitsOnly(event.clipboardData.getData('text')).length >= length ? 0 : index, event.clipboardData.getData('text')) }}
              onChange={(event) => {
                const incoming = digitsOnly(event.currentTarget.value)
                if (incoming.length > 1) return fillFrom(index, incoming)
                const next = [...chars]
                next[index] = incoming
                commit(next)
                if (incoming && index < length - 1) focusAt(index + 1)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Backspace') {
                  event.preventDefault()
                  const next = [...chars]
                  if (next[index]) next[index] = ''
                  else if (index > 0) { next[index - 1] = ''; focusAt(index - 1) }
                  commit(next)
                } else if (event.key === 'ArrowLeft') { event.preventDefault(); focusAt(index - 1) }
                else if (event.key === 'ArrowRight') { event.preventDefault(); focusAt(index + 1) }
                else if (event.key === 'Home') { event.preventDefault(); focusAt(0) }
                else if (event.key === 'End') { event.preventDefault(); focusAt(length - 1) }
              }}
              className={cn('h-12 w-10 rounded-[10px] border-0 bg-bg text-center text-transparent caret-transparent hairline outline-none transition-[background-color,box-shadow] selection:bg-transparent disabled:opacity-50', error ? 'outline-2 outline-danger' : focused === index ? 'outline-2 outline-ring' : '')}
            />
            <span aria-hidden="true" className="pointer-events-none absolute inset-0 grid place-items-center">
              <AnimatePresence initial={false} mode="popLayout">
                {char && <motion.span key={char} initial={reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -3 }} transition={reduced ? INSTANT : CROSSFADE} className="col-start-1 row-start-1 font-mono text-base tabular-nums text-fg">{char}</motion.span>}
              </AnimatePresence>
              {focused === index && !char && !disabled && <motion.span className="col-start-1 row-start-1 h-4 w-px bg-fg" animate={reduced ? undefined : { opacity: [1, 1, 0, 0] }} transition={reduced ? undefined : { duration: 1.06, times: [0, 0.5, 0.5, 1], repeat: Infinity }} />}
            </span>
          </div>
        ))}
      </motion.div>
      <div className="mt-2 min-h-4 text-xs/4 text-danger">
        {error && <motion.p id={statusId} role="status" initial={reduced ? false : { opacity: 0, y: -2 }} animate={{ opacity: 1, y: 0 }}>{Array.isArray(error) ? error.join(', ') : error}</motion.p>}
      </div>
    </div>
  )
}

type Validate = (value: string) => string | null | undefined

function useInlineValidation(value: string, validate: Validate, debounce = 300) {
  const [touched, setTouched] = useState(false)
  const [error, setError] = useState<string | null | undefined>(null)
  useEffect(() => {
    if (!touched) return undefined
    const next = validate(value)
    const timer = setTimeout(() => setError(next), debounce)
    return () => clearTimeout(timer)
  }, [value, validate, debounce, touched])
  const commit = () => { setTouched(true); setError(validate(value)) }
  return { error, commit, valid: touched && value.length > 0 && !error }
}

export type InlineValidationProps = {
  label: ReactNode
  value: string
  onChange: (value: string) => void
  validate: Validate
  error?: string | string[] | null
  hint?: ReactNode
  name?: string
  type?: HTMLInputTypeAttribute
  placeholder?: string
  autoComplete?: HTMLInputAutoCompleteAttribute
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode']
  autoFocus?: boolean
  required?: boolean
}

export function InlineValidation({ label, value, onChange, validate, error: externalError, hint, name, type = 'text', placeholder, autoComplete, inputMode, autoFocus = false, required = false }: InlineValidationProps) {
  const reduced = useReducedMotion()
  const local = useInlineValidation(value, validate)
  const error = externalError || local.error
  const invalid = Boolean(error)
  const id = useId()
  const statusId = `${id}-status`
  return (
    <label className="flex flex-col gap-1.5">
      <span className="field-label">{label}</span>
      <span className="relative">
        <input id={id} name={name} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete} inputMode={inputMode} autoFocus={autoFocus} required={required} aria-invalid={invalid || undefined} aria-describedby={(error || hint) ? statusId : undefined} onBlur={local.commit} onChange={(event) => onChange(event.target.value)} className="control-input pr-9" />
        <span className="pointer-events-none absolute top-1/2 right-3 grid size-3.5 -translate-y-1/2 place-items-center" aria-hidden="true">
          <motion.svg viewBox="0 0 12 12" className="col-start-1 row-start-1 size-3.5 text-success" fill="none" initial={false} animate={{ opacity: local.valid && !externalError ? 1 : 0, scale: local.valid && !externalError ? 1 : 0.7 }} transition={reduced ? INSTANT : CROSSFADE}><path d="M2 6.3 4.7 9 10 3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></motion.svg>
          <motion.svg viewBox="0 0 12 12" className="col-start-1 row-start-1 size-3.5 text-danger" fill="none" initial={false} animate={{ opacity: invalid ? 1 : 0, scale: invalid ? 1 : 0.7 }} transition={reduced ? INSTANT : CROSSFADE}><path d="M6 2v4.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><circle cx="6" cy="9" r="0.9" fill="currentColor" /></motion.svg>
        </span>
      </span>
      <span id={statusId} role="status" aria-live="polite" className={cn('field-hint min-h-5', invalid && 'text-danger')}>{error ? (Array.isArray(error) ? error.join(', ') : error) : hint}</span>
    </label>
  )
}

export type NavigationTabItem<K extends string = string> = {
  key: K
  label: ReactNode
  disabled?: boolean
}

export type NavigationTabsProps<K extends string = string> = {
  items: NavigationTabItem<K>[]
  value: K
  onChange: (key: K) => void
  label?: string
  divider?: boolean
  inset?: boolean
  surface?: boolean
  className?: string
}

export function NavigationTabs<K extends string = string>({ items, value, onChange, label, divider = true, inset = true, surface = true, className = '' }: NavigationTabsProps<K>) {
  const reduced = useReducedMotion()
  const id = useId()
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const current = Math.max(0, items.findIndex((item) => item.key === value))
  const select = (index: number) => {
    const item = items[index]
    if (!item || item.disabled) return
    refs.current[index]?.focus()
    onChange(item.key)
  }
  const seek = (from: number, direction: number) => {
    let index = from
    for (let step = 0; step < items.length; step += 1) {
      index = (index + direction + items.length) % items.length
      if (!items[index].disabled) return index
    }
    return from
  }
  return (
    <div role="tablist" aria-label={label} className={cn('relative flex h-12 max-w-full shrink-0 items-center gap-1 overflow-x-auto', surface && 'chrome-toolbar', inset && 'px-4 sm:px-6', divider && 'border-b border-line', className)}>
      {items.map((item, index) => (
        <button key={item.key} ref={(node) => { refs.current[index] = node }} type="button" role="tab" aria-selected={value === item.key} tabIndex={value === item.key ? 0 : -1} disabled={item.disabled} onClick={() => onChange(item.key)} onKeyDown={(event) => {
          if (['ArrowRight', 'ArrowDown'].includes(event.key)) { event.preventDefault(); select(seek(index, 1)) }
          else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) { event.preventDefault(); select(seek(index, -1)) }
          else if (event.key === 'Home') { event.preventDefault(); select(seek(items.length - 1, 1)) }
          else if (event.key === 'End') { event.preventDefault(); select(seek(0, -1)) }
        }} className={cn('relative flex h-8 shrink-0 items-center rounded-pill px-3.5 text-sm/5 font-medium capitalize outline-none hover:text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring disabled:opacity-45', value === item.key ? 'text-fg' : 'text-muted-600')}>
          {value === item.key && <motion.span layoutId={`space-view-indicator-${id}`} aria-hidden="true" className="chrome-selection absolute inset-0 rounded-pill" transition={reduced ? INSTANT : { duration: 0.18, ease: [0.22, 1, 0.36, 1] }} />}
          <span className="relative">{item.label}</span>
        </button>
      ))}
    </div>
  )
}

// A modal sheet adapted from Interior's Drawer interaction model: a quick, interruptible
// transition, a separate scrim, reduced-motion support, focus containment, Escape dismissal, and an
// inert background. Positioning and surfaces stay in Sunny's design system.
const SHEET_ENTER: Transition = { type: 'tween', duration: 0.2, ease: [0.22, 1, 0.36, 1] }
const SHEET_EXIT: Transition = { ...SHEET_ENTER, duration: 0.15 }

function useDesktopSheet() {
  const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches)
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)')
    const update = () => setDesktop(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return desktop
}

export type SheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onExitComplete?: () => void
  backgroundRef?: RefObject<HTMLElement | null>
  label: string
  viewport?: boolean
  compact?: boolean
  className?: string
  children?: ReactNode
}

export function Sheet({ open, onOpenChange, onExitComplete, backgroundRef, label, viewport = false, compact = false, className = '', children }: SheetProps) {
  const panel = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()
  const desktop = useDesktopSheet()

  useEffect(() => {
    if (!open) return undefined
    const background = backgroundRef?.current
    const previousOverflow = document.body.style.overflow
    if (background) {
      background.inert = true
      background.setAttribute('aria-hidden', 'true')
    }
    document.body.style.overflow = 'hidden'
    return () => {
      if (background) {
        background.inert = false
        background.removeAttribute('aria-hidden')
      }
      document.body.style.overflow = previousOverflow
    }
  }, [backgroundRef, open])

  useEffect(() => {
    if (!open) return undefined
    const previous = document.activeElement
    const focusPanel = requestAnimationFrame(() => panel.current?.focus({ preventScroll: true }))
    const onKeyDown = (event: KeyboardEvent) => {
      // A Page can open a task or browser sign-in sheet. Only the topmost
      // available dialog owns Escape and the focus loop while they are stacked.
      const dialogs = [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')]
        .filter((node) => node.getClientRects().length > 0 && !node.closest('[inert]'))
      if (event.defaultPrevented || dialogs.at(-1) !== panel.current) return
      if (event.key === 'Escape') {
        if (panel.current?.querySelector('[role="dialog"]')) return
        event.preventDefault()
        onOpenChange(false)
        return
      }
      if (event.key !== 'Tab' || !panel.current) return
      const focusable = [...panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => node.getClientRects().length > 0)
      if (!focusable.length) {
        event.preventDefault()
        panel.current.focus({ preventScroll: true })
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (document.activeElement === panel.current) {
        event.preventDefault()
        const target = event.shiftKey ? last : first
        target.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(focusPanel)
      document.removeEventListener('keydown', onKeyDown)
      if (previous instanceof HTMLElement && document.contains(previous)) previous.focus({ preventScroll: true })
    }
  }, [onOpenChange, open])

  const hidden = desktop ? { opacity: 0, x: 32, scale: 0.992 } : { opacity: 0, y: 36, scale: 0.996 }
  const transition = reduced ? INSTANT : SHEET_ENTER
  const dimensions = compact
    ? 'top-1.5 right-1.5 h-[min(620px,calc(100%-12px))] w-[calc(100%-12px)] sm:top-3 sm:right-3 sm:h-[min(640px,calc(100%-24px))] sm:w-[min(390px,calc(100%-24px))]'
    : 'inset-y-1.5 right-1.5 w-[calc(100%-12px)] sm:inset-y-2 sm:right-2 sm:w-[calc(100%-16px)] lg:w-[min(920px,calc(100%-24px))]'

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {open && (
        <div className={cn(viewport ? 'fixed' : 'absolute', 'inset-0 z-40')}>
          <motion.button
            type="button"
            tabIndex={-1}
            aria-label={`Close ${label}`}
            className="absolute inset-0 cursor-default bg-fg/[0.045]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: reduced ? INSTANT : { duration: 0.12, ease: 'easeOut' } }}
            transition={reduced ? INSTANT : { duration: 0.15, ease: 'easeOut' }}
            onClick={() => onOpenChange(false)}
          />
          <motion.section
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            tabIndex={-1}
            style={{ outline: 'none' }}
            initial={reduced ? { opacity: 0 } : hidden}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0, transition: INSTANT } : { ...hidden, transition: SHEET_EXIT }}
            transition={transition}
            className={cn('elevated-surface absolute flex flex-col overflow-hidden rounded-window bg-bg outline-none focus-visible:outline-none', dimensions, className)}
          >
            {children}
          </motion.section>
        </div>
      )}
    </AnimatePresence>
  )
}
